import { ErrorMessageTranslator } from '../errors/ErrorMessageTranslator'

export interface ErrorInfo {
  line: number
  column?: number
  message: string
  type?: 'syntax' | 'runtime' | 'logic'
}

export class ErrorDisplayPanel {
  private container: HTMLElement
  private errors: ErrorInfo[] = []
  private isVisible: boolean = false
  
  constructor() {
    this.container = this.createContainer()
    this.setupStyles()
  }
  
  /**
   * コンテナを作成
   */
  private createContainer(): HTMLElement {
    const container = document.createElement('div')
    container.className = 'error-display-panel'
    container.innerHTML = `
      <div class="error-panel__header">
        <h3 class="error-panel__title">
          <span class="error-panel__icon">⚠️</span>
          エラー
        </h3>
        <button class="error-panel__close" title="閉じる">×</button>
      </div>
      <div class="error-panel__content"></div>
    `
    
    // メインコンテンツエリアに追加
    const mainContent = document.querySelector('.main-content')
    if (mainContent) {
      mainContent.appendChild(container)
    }
    
    // 閉じるボタンのイベント
    const closeBtn = container.querySelector('.error-panel__close')
    closeBtn?.addEventListener('click', () => this.hide())
    
    return container
  }
  
  /**
   * スタイルを設定
   */
  private setupStyles(): void {
    const style = document.createElement('style')
    style.textContent = `
      .error-display-panel {
        position: absolute;
        top: 1rem;
        right: 1rem;
        width: 400px;
        max-width: calc(100% - 2rem);
        background: white;
        border-radius: 0.5rem;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
        border: 1px solid var(--error-color);
        opacity: 0;
        transform: translateY(-20px) scale(0.95);
        transition: all 0.3s ease;
        visibility: hidden;
        z-index: 1000;
        max-height: 400px;
        display: flex;
        flex-direction: column;
      }
      
      .error-display-panel.visible {
        opacity: 1;
        transform: translateY(0) scale(1);
        visibility: visible;
      }
      
      .error-panel__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 1rem;
        border-bottom: 1px solid #fee;
        background: #fef2f2;
        border-radius: 0.5rem 0.5rem 0 0;
      }
      
      .error-panel__title {
        margin: 0;
        font-size: 1rem;
        font-weight: 600;
        color: var(--error-color);
        display: flex;
        align-items: center;
        gap: 0.5rem;
      }
      
      .error-panel__icon {
        font-size: 1.25rem;
        animation: errorPulse 2s ease-in-out infinite;
      }
      
      @keyframes errorPulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.6; }
      }
      
      .error-panel__close {
        background: none;
        border: none;
        font-size: 1.5rem;
        color: #dc2626;
        cursor: pointer;
        padding: 0;
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 0.25rem;
        transition: all 0.2s ease;
      }
      
      .error-panel__close:hover {
        background: rgba(220, 38, 38, 0.1);
        transform: scale(1.1);
      }
      
      .error-panel__content {
        padding: 1rem;
        overflow-y: auto;
        flex: 1;
      }
      
      .error-item {
        margin-bottom: 1rem;
        padding: 0.75rem;
        background: #fef2f2;
        border: 1px solid #fee;
        border-radius: 0.375rem;
        animation: errorItemSlide 0.3s ease-out;
      }
      
      @keyframes errorItemSlide {
        from {
          opacity: 0;
          transform: translateX(-20px);
        }
        to {
          opacity: 1;
          transform: translateX(0);
        }
      }
      
      .error-item:last-child {
        margin-bottom: 0;
      }
      
      .error-item__header {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        margin-bottom: 0.5rem;
      }
      
      .error-item__location {
        font-family: 'Monaco', 'Menlo', monospace;
        font-size: 0.75rem;
        color: #dc2626;
        background: white;
        padding: 0.125rem 0.375rem;
        border-radius: 0.25rem;
        border: 1px solid #fee;
      }
      
      .error-item__type {
        font-size: 0.75rem;
        padding: 0.125rem 0.375rem;
        border-radius: 0.25rem;
        font-weight: 500;
      }
      
      .error-item__type--syntax {
        background: #fbbf24;
        color: #92400e;
      }
      
      .error-item__type--runtime {
        background: #ef4444;
        color: white;
      }
      
      .error-item__type--logic {
        background: #8b5cf6;
        color: white;
      }
      
      .error-item__message {
        font-size: 0.875rem;
        color: #1f2937;
        margin-bottom: 0.5rem;
        font-weight: 500;
      }
      
      .error-item__suggestion {
        font-size: 0.8125rem;
        color: #6b7280;
        margin-bottom: 0.5rem;
        padding-left: 1rem;
        position: relative;
      }
      
      .error-item__suggestion::before {
        content: "💡";
        position: absolute;
        left: 0;
      }
      
      .error-item__example {
        font-family: 'Monaco', 'Menlo', monospace;
        font-size: 0.75rem;
        background: #f3f4f6;
        padding: 0.5rem;
        border-radius: 0.25rem;
        color: #374151;
        margin-top: 0.5rem;
        border: 1px solid #e5e7eb;
      }
      
      .error-item__example::before {
        content: "例: ";
        font-weight: 600;
        color: #6b7280;
      }
      
      /* エラーがない場合 */
      .error-panel__empty {
        text-align: center;
        color: #9ca3af;
        padding: 2rem;
        font-size: 0.875rem;
      }
      
      /* モバイル対応 */
      @media (max-width: 768px) {
        .error-display-panel {
          width: calc(100% - 2rem);
          top: auto;
          bottom: 1rem;
          max-height: 50vh;
        }
      }
      
      /* シェイクアニメーション */
      .error-display-panel.shake {
        animation: panelShake 0.5s ease-in-out;
      }
      
      @keyframes panelShake {
        0%, 100% { transform: translateX(0); }
        10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
        20%, 40%, 60%, 80% { transform: translateX(5px); }
      }
    `
    document.head.appendChild(style)
  }
  
  /**
   * エラーを表示
   */
  showErrors(errors: ErrorInfo[]): void {
    this.errors = errors
    this.render()
    this.show()
    
    // 新しいエラーが追加されたらシェイクアニメーション
    if (errors.length > 0) {
      this.container.classList.add('shake')
      setTimeout(() => {
        this.container.classList.remove('shake')
      }, 500)
    }
  }
  
  /**
   * エラーをクリア
   */
  clearErrors(): void {
    this.errors = []
    this.render()
    if (this.errors.length === 0) {
      setTimeout(() => this.hide(), 300)
    }
  }
  
  /**
   * レンダリング
   */
  private render(): void {
    const content = this.container.querySelector('.error-panel__content')
    if (!content) return
    
    if (this.errors.length === 0) {
      content.innerHTML = `
        <div class="error-panel__empty">
          エラーはありません ✅
        </div>
      `
      return
    }
    
    content.innerHTML = this.errors.map((error, index) => {
      const translated = ErrorMessageTranslator.translate(error.message)
      const errorType = error.type || ErrorMessageTranslator.getErrorType(error.message)
      
      return `
        <div class="error-item" style="animation-delay: ${index * 0.1}s">
          <div class="error-item__header">
            <span class="error-item__location">
              行 ${error.line}${error.column ? `:${error.column}` : ''}
            </span>
            <span class="error-item__type error-item__type--${errorType}">
              ${this.getErrorTypeLabel(errorType)}
            </span>
          </div>
          <div class="error-item__message">${translated.message}</div>
          <div class="error-item__suggestion">${translated.suggestion}</div>
          ${translated.example ? `
            <div class="error-item__example">${this.escapeHtml(translated.example)}</div>
          ` : ''}
        </div>
      `
    }).join('')
  }
  
  /**
   * エラータイプのラベルを取得
   */
  private getErrorTypeLabel(type: string): string {
    const labels: Record<string, string> = {
      syntax: '構文エラー',
      runtime: '実行時エラー',
      logic: 'ロジックエラー'
    }
    return labels[type] || 'エラー'
  }
  
  /**
   * HTMLをエスケープ
   */
  private escapeHtml(text: string): string {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
  
  /**
   * パネルを表示
   */
  private show(): void {
    this.isVisible = true
    setTimeout(() => {
      this.container.classList.add('visible')
    }, 10)
  }
  
  /**
   * パネルを非表示
   */
  hide(): void {
    this.isVisible = false
    this.container.classList.remove('visible')
  }
  
  /**
   * 表示状態を取得
   */
  getIsVisible(): boolean {
    return this.isVisible
  }
}